import { parseLights, replay } from "./board";
import {
  MAX_PRESSES,
  type LightsOutDefinition,
  type LightsOutProgress,
  type LightsOutSubmission,
  type Press,
} from "./types";

const DEFAULT_RULES = {
  en: "Every press toggles a light and its four neighbours — lit goes dark, dark comes on. Switch every light off.",
  fr: "Chaque pression bascule une lampe et ses quatre voisines : allumée, elle s'éteint ; éteinte, elle s'allume. Éteignez toutes les lampes.",
};

/** The same starting board as the backend's `empty_definition()`: one press in the middle. */
export const STARTER_LIGHTS = ["00000", "00100", "01110", "00100", "00000"];

export function emptyDefinition(): LightsOutDefinition {
  return {
    puzzleType: "lights_out",
    typeVersion: 1,
    size: STARTER_LIGHTS.length,
    lights: [...STARTER_LIGHTS],
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: LightsOutDefinition): LightsOutProgress {
  return { version: 1, presses: [], undone: [] };
}

/** The legal prefix of whatever is stored, capped. */
export function legalPresses(definition: LightsOutDefinition, raw: unknown): Press[] {
  if (!Array.isArray(raw)) return [];
  const outcome = replay(definition.size, parseLights(definition.lights), raw);
  return outcome.presses.slice(0, MAX_PRESSES);
}

/**
 * Accepts any stored blob (older versions, null, garbage) and returns presses this board can
 * play: a log recorded against another size is cut at the first press that falls off it.
 */
export function migrateProgress(definition: LightsOutDefinition, raw: unknown): LightsOutProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = raw as { presses?: unknown; undone?: unknown };
  const presses = legalPresses(definition, source.presses);
  const whole = Array.isArray(source.presses) && presses.length === source.presses.length;
  return { version: 1, presses, undone: whole ? legalPresses(definition, source.undone) : [] };
}

/** The server replays the same list: what the player pressed, and nothing else. */
export function serializeSubmission(
  _definition: LightsOutDefinition,
  progress: LightsOutProgress,
): LightsOutSubmission {
  return { presses: progress.presses.map(([r, c]) => [r, c]) };
}
